import useEventListener from '@use-it/event-listener';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { allow } from '../../common/classes/allow';
import EnterPasswordSection from '../../common/components/account-trouble/enter.password.section';
import Button from '../../common/components/button';
import Div from '../../common/components/div';
import DropDown from '../../common/components/dropdown';
import H1 from '../../common/components/h1';
import H2 from '../../common/components/h2';
import H3 from '../../common/components/h3';
import H6 from '../../common/components/h6';
import InputField from '../../common/components/input.field';
import MDAndLargerDiv from '../../common/components/md.and.larger.div';
import StackingButtons from '../../common/components/stacking.buttons';
import TranslatedSpan from '../../common/components/translated.span';
import ValidationAlertMessage from '../../common/components/validation.alert.message';
import ValidationAlerts from '../../common/components/validation.alerts';
import XSAndSMDiv from '../../common/components/xs.and.sm.div';
import translate from '../../common/functions/translate';
import { useConversion, useFormStart } from '../../common/hooks/use.analytics';
import useTrait from '../../common/hooks/use.trait';
import is from '../../common/objects/is';
import the from '../../common/objects/the';
import use from '../../common/objects/use';
import { Caret } from '@cmsgov/ds-medicare-gov';
import ApiErrorAlert from '../../common/components/api.error.alert';
import { get400InvalidUsernameOrPasswordError } from '../../common/functions/get.api.error.message';

const YourAccountDetails = () => {
    const [confirmPasswordRef, setConfirmPasswordRef] = useState();
    const [passwordRef, setPasswordRef] = useState();
    const [secretAnswerRef, setSecretAnswerRef] = useState();
    const [secretQuestionRef, setSecretQuestionRef] = useState();
    const [usernameRef, setUsernameRef] = useState();
    const apiErrorMessage = useTrait(null);
    const confirmPasswordError = useTrait(false);
    const passwordError = useTrait(false);
    const passwordGuidelinesExpanded = useTrait(false);
    const secretAnswerError = useTrait(false);
    const secretQuestionError = useTrait(false);
    const showPasswords = useTrait(false);
    const usernameError = useTrait(false);
    const usernameGuidelinesExpanded = useTrait(false);
    const validateEntireForm = useTrait(false);
    const validationMessages = useTrait([]);
    const [isLoading, setIsLoading] = useState(false);
   
    const formStart = useFormStart('sign_up');
    const conversion = useConversion('create account', 'sign_up');
    const history = useHistory();
   
    const api = use.createAccountApi;
    const data = use.createAccountData;
   
    const callCreateAccount = async () => {
        const response = await api.step3(data.username.get(), data.password.get(), data.secretQuestion.get(), data.secretAnswer.get());
        setIsLoading(false);
        const statusCode = response?.status;
        validateEntireForm.set(false);
        let success = false;
        switch (statusCode) {
                case 204:
                    success = true;
                    conversion.success();
                    use.global.showNewAccountLoginAlert.set(true);
                    history.push('/login');
                    data.reset(); // reset after redirect so we don't fire off an extra page view event for step 1
                    break;
                case 400:
                    reset();
                    const invalidUsernameOrPasswordMessage = get400InvalidUsernameOrPasswordError(response?.data?.errors);
                    if (invalidUsernameOrPasswordMessage) {
                        apiErrorMessage.set(invalidUsernameOrPasswordMessage);
                    } else {
                        apiErrorMessage.set('We couldn\'t process your request. Please re-enter your information and try again.');
                    }
                    break;
                case 409:
                    reset();
                    apiErrorMessage.set('The username that you supplied is not available.  Please choose a different username.');
                    break;
                default:
                    reset();
                    apiErrorMessage.set('We can\'t process your request at this time. We\'re sorry for any inconvenience. Please try again.');
                    break;
        }
   
        if (!success) {
            const errorMessage = apiErrorMessage.get() || 'unkown error';
            conversion.failAPIIntermediate(errorMessage);
            conversion.failAPI(errorMessage);
        }
    };
   
    const getApiErrorMessage = () => {
        if (!apiErrorMessage.get())
            return null;
        return translate(apiErrorMessage.get());
    };
   
    const getBackButton = () => {
        return <>
            <Button
                color={'teal'}
                displayAs={'button'}
                fill={'outlined'}
                onClick={data.stepBackward}
                size={'small'}
            >
                <TranslatedSpan english={'Back'}/>
            </Button>
        </>;
    };
   
    const getConfirmPasswordSection = () => {
        return <>
            <EnterPasswordSection
                columnWidth={12}
                errorMessage={confirmPasswordError.get() ? `${translate('Error')}: ${translate('Re-enter your new password.')}` : null}
                inputRef={setConfirmPasswordRef}
                label={translate('Confirm Password')}
                onBlur={event => updateConfirmPassword(event.currentTarget.value, is.to.be.validated)}
                onChange={event => updateConfirmPassword(event.currentTarget.value)}
                onShowPasswordChange={() => showPasswords.set(!showPasswords.get())}
                showForgotPasswordLink={false}
                showPassword={showPasswords.get()}
                value={data.confirmPassword.get()}
            />
        </>;
    };
   
    const getCreateAccountButton = () => {
        return <>
            <Button
                color={'teal'}
                displayAs={'button'}
                fill={'solid'}
                onClick={handleCreateAccountButtonClick}
                size={'small'}
                isLoading={isLoading}
            >
                <TranslatedSpan english={'Create Account'}/>
            </Button>
        </>;
    };
   
    const getPasswordGuidelines = () => {
        return <>
            <p>
                <TranslatedSpan english={'Do\'s'}/>
            </p>
            <ul
                className={'ds-c-list'}
                style={{paddingLeft: 24}}
            >
                <li style={{paddingLeft: 10}}>
                    <TranslatedSpan english={'Must be 8-16 characters long'}/>
                </li>
                <li style={{paddingLeft: 10}}>
                    <TranslatedSpan english={'Must have at least 1 letter'}/>
                </li>
                <li style={{paddingLeft: 10}}>
                    <TranslatedSpan english={'Must have at least 1 number'}/>
                </li>
                <li style={{paddingLeft: 10}}>
                    <TranslatedSpan english={'Must have 1 or more of only these special characters: @ ! $ % ^ * ( )'}/>
                </li>
                <li>
                    <TranslatedSpan english={'Must be different from your last 6 passwords'}/>
                </li>
            </ul>
            <p>
                <TranslatedSpan english={'Don\'ts'}/>
            </p>
            <ul
                className={'ds-c-list'}
                style={{paddingLeft: 24}}
            >
                <li style={{paddingLeft: 10}}>
                    <TranslatedSpan english={`Can't contain your username`}/>
                </li>
                <li style={{paddingLeft: 10}}>
                    <TranslatedSpan english={`Can't include your Medicare Number or Social Security number`}/>
                </li>
            </ul>
        </>;
    };
   
    const getPasswordSection = () => {
        return <>
            <EnterPasswordSection
                columnWidth={12}
                errorMessage={passwordError.get() ? `${translate('Error')}: ${translate('Create a password for your account using the guidelines listed.')}` : null}
                inputRef={setPasswordRef}
                label={translate('Password')}
                onBlur={event => updatePassword(event.currentTarget.value, is.to.be.validated)}
                onChange={event => updatePassword(event.currentTarget.value)}
                onFocus={() => passwordGuidelinesExpanded.set(true)}
                onShowPasswordChange={() => showPasswords.set(!showPasswords.get())}
                showForgotPasswordLink={false}
                showPassword={showPasswords.get()}
                value={data.password.get()}
            />
        </>;
    };
   
    const getSecretAnswerInputField = () => {
        return <>
            <InputField
                aria-invalid={secretAnswerError.get().toString()}
                errorMessage={secretAnswerError.get() ? `${translate('Error')}: ${translate('Please enter a secret answer.')}` : null}
                inputRef={setSecretAnswerRef}
                label={translate('Secret answer')}
                name={'secretAnswer'}
                onClick={formStart}
                onBlur={event => updateSecretAnswer(event.currentTarget.value, is.to.be.validated)}
                onChange={event => updateSecretAnswer(event.currentTarget.value)}
                style={{minWidth: '100%'}}
                value={data.secretAnswer.get()}
            />
        </>;
    };
   
    const getSecretQuestionDropdown = () => {
        return <>
            <DropDown
                aria-invalid={secretQuestionError.get().toString()}
                errorMessage={secretQuestionError.get() ? `${translate('Error')}: ${translate('Please select a secret question.')}` : null}
                inputRef={setSecretQuestionRef}
                label={translate('Secret Question')}
                name={'secretQuestion'}
                onBlur={event => updateSecretQuestion(event.currentTarget.value, is.to.be.validated)}
                onChange={event => updateSecretQuestion(event.currentTarget.value)}
                options={questionList}
                style={{
                    color: the.color.smokyGrey,
                    minWidth: '100%',
                }}
                value={data.secretQuestion.get()}
            />
        </>;
    };
   
    const getSecureYourAccountH3 = () => {
        return <>
            <H3>
                <TranslatedSpan english={'Secure your account'}/>
            </H3>
        </>;
    };
   
    const getUsernameGuidelines = () => {
        return <>
            <p>
                <TranslatedSpan english={'Do\'s'}/>
            </p>
            <ul
                className={'ds-c-list'}
                style={{paddingLeft: 24}}
            >
                <li style={{paddingLeft: 10}}>
                    <TranslatedSpan english={'Must be 8-30 characters long with no spaces'}/>
                </li>
                <li style={{paddingLeft: 10}}>
                    <TranslatedSpan english={'Can have letters, numbers, and these special characters: @ ! . - _ $'}/>
                </li>
                <li style={{paddingLeft: 10}}>
                    <TranslatedSpan english={'Must have at least 4 letters'}/>
                </li>
                <li style={{paddingLeft: 10}}>
                    <TranslatedSpan english={'Can be your email address'}/>
                </li>
            </ul>
            <p>
                <TranslatedSpan english={'Don\'ts'}/>
            </p>
            <ul
                className={'ds-c-list'}
                style={{paddingLeft: 24}}
            >
                <li style={{paddingLeft: 10}}>
                    <TranslatedSpan english={`Can't include your Medicare Number or Social Security number`}/>
                </li>
                <li style={{paddingLeft: 10}}>
                    <TranslatedSpan english={'Can’t be the same as your password'}/>
                </li>
                <li style={{paddingLeft: 10}}>
                    <TranslatedSpan english={'Can’t have a special character as the first or last character'}/>
                </li>
            </ul>
        </>;
    };
   
    const getUsernameInputField = () => {
        return <>
            <InputField
                aria-invalid={usernameError.get().toString()}
                errorMessage={usernameError.get() ? `${translate('Error')}: ${translate('Create a username for your account using the guidelines listed.')}` : null}
                inputRef={setUsernameRef}
                label={translate('Username')}
                name={'username'}
                onClick={formStart}
                onBlur={event => updateUsername(event.currentTarget.value, is.to.be.validated)}
                onChange={event => updateUsername(event.currentTarget.value)}
                onFocus={() => usernameGuidelinesExpanded.set(true)}
                style={{minWidth: '100%'}}
                value={data.username.get()}
            />
        </>;
    };
   
    const getValidationAlertMessages = () => {
        const messages = [];
        if (!validateEntireForm.get())
            return messages;
        if (data.username.get() === '')
            messages.push(
                <ValidationAlertMessage
                    description={translate('Create a username for your account using the guidelines listed.')}
                    fieldReference={usernameRef}
                    linkText={translate('Username')}
                />
            );
        if (data.password.get() === '')
            messages.push(
                <ValidationAlertMessage
                    description={translate('Create a password for your account using the guidelines listed.')}
                    fieldReference={passwordRef}
                    linkText={translate('Password')}
                />
            );
        if (data.confirmPassword.get() !== data.password.get())
            messages.push(
                <ValidationAlertMessage
                    description={translate('Re-enter your new password.')}
                    fieldReference={passwordRef}
                    linkText={translate('Confirm Password')}
                />
            );
        if (data.secretQuestion.get() === '')
            messages.push(
                <ValidationAlertMessage
                    description={translate('Please select a secret question.')}
                    fieldReference={passwordRef}
                    linkText={translate('Secret Question')}
                />
            );
        if (data.secretAnswer.get() === '')
            messages.push(
                <ValidationAlertMessage
                    description={translate('Please enter a secret answer.')}
                    fieldReference={passwordRef}
                    linkText={translate('Secret answer')}
                />
            );
        return messages;
    };
   
    const handleCreateAccountButtonClick = () => {
        if (isLoading) {
            return;
        }
        setIsLoading(true);
   
        conversion.attempt();
        const errorFieldRef = validateForm();
        if (errorFieldRef !== null) {
            errorFieldRef.focus();
            setIsLoading(false);
            return;
        }
        callCreateAccount();
    };
   
    const questionList = [
        {label: translate('Select question'), value: ''},
        {label: translate('What is your favorite vacation spot?'), value: 'What is your favorite vacation spot?'},
        {label: translate('In what city did you first meet your spouse?'), value: 'In what city did you first meet your spouse?'},
        {label: translate('What country would you most like to visit?'), value: 'What country would you most like to visit?'},
        {label: translate('What is the title of your favorite book?'), value: 'What is the title of your favorite book?'},
        {label: translate('What is the name of the first street you lived on?'), value: 'What is the name of the first street you lived on?'},
        {label: translate('What was the name of your first pet?'), value: 'What was the name of your first pet?'},
        {label: translate(`What is your best friend's last name?`), value: `What is your best friend's last name?`}
    ];
   
    const reset = () => {
        confirmPasswordError.set(false);
        passwordError.set(false);
        secretAnswerError.set(false);
        secretQuestionError.set(false);
        usernameError.set(false);
    };
   
    const updateConfirmPassword = (value = '', doImmediateValidation = false) => {
        allow.aString(value).aBoolean(doImmediateValidation);
        formStart();
        const newValue = value.trimStart();
        data.confirmPassword.set(newValue);
        if (!doImmediateValidation && !validateEntireForm.get())
            return;
        validateConfirmPassword();
        if (validateEntireForm.get())
            validateForm();
    };
   
    const updatePassword = (value = '', doImmediateValidation = false) => {
        allow.aString(value).aBoolean(doImmediateValidation);
        formStart();
        const newValue = value.trimStart();
        data.password.set(newValue);
        if (!doImmediateValidation && !validateEntireForm.get())
            return;
        validatePassword();
        if (validateEntireForm.get())
            validateForm();
    };
   
    const updateSecretAnswer = (value = '', doImmediateValidation = false) => {
        allow.aString(value).aBoolean(doImmediateValidation);
        formStart();
        const newValue = value.trimStart();
        data.secretAnswer.set(newValue);
        if (!doImmediateValidation && !validateEntireForm.get())
            return;
        validateSecretAnswer();
        if (validateEntireForm.get())
            validateForm();
    };
   
    const updateSecretQuestion = (value = '', doImmediateValidation = false) => {
        allow.aString(value).aBoolean(doImmediateValidation);
        formStart();
        const newValue = value.trimStart();
        data.secretQuestion.set(newValue);
        if (!doImmediateValidation && !validateEntireForm.get())
            return;
        validateSecretQuestion();
        if (validateEntireForm.get())
            validateForm();
    };
   
    const updateUsername = (value = '', doImmediateValidation = false) => {
        allow.aString(value).aBoolean(doImmediateValidation);
        formStart();
        const newValue = value.trimStart();
        data.username.set(newValue);
        if (!doImmediateValidation && !validateEntireForm.get())
            return;
        validateUsername();
        if (validateEntireForm.get())
            validateForm();
    };
   
    const validateConfirmPassword = () => {
        const isValid = data.confirmPassword.get() === data.password.get();
        if (confirmPasswordError.get() === isValid)
            confirmPasswordError.set(!isValid);
        return isValid;
    };
   
    const validateForm = () => {
        if (!validateEntireForm.get())
            validateEntireForm.set(true);
        let errorFieldRef = null;
        if (!validateUsername())
            errorFieldRef = usernameRef;
        if (!validatePassword() && !errorFieldRef)
            errorFieldRef = passwordRef;
        if (!validateConfirmPassword() && !errorFieldRef)
            errorFieldRef = confirmPasswordRef;
        if (!validateSecretQuestion() && !errorFieldRef)
            errorFieldRef = secretQuestionRef;
        if (!validateSecretAnswer() && !errorFieldRef)
            errorFieldRef = secretAnswerRef;
        const messages = getValidationAlertMessages();
        if (messages.length) {
            const errorString = messages.map(x => x.props.description).join(', ');
            conversion.failValidation(errorString);
        }
        return messages.length ? errorFieldRef : null;
    };
   
    const validatePassword = () => {
        const isValid = data.password.get() !== '';
        if (passwordError.get() === isValid)
            passwordError.set(!isValid);
        return isValid;
    };
   
    const validateSecretAnswer = () => {
        const isValid = data.secretAnswer.get() !== '';
        if (secretAnswerError.get() === isValid)
            secretAnswerError.set(!isValid);
        return isValid;
    };
   
    const validateSecretQuestion = () => {
        const isValid = data.secretQuestion.get() !== '';
        if (secretQuestionError.get() === isValid)
            secretQuestionError.set(!isValid);
        return isValid;
    };
   
    const validateUsername = () => {
        const isValid = data.username.get() !== '';
        if (usernameError.get() === isValid)
            usernameError.set(!isValid);
        return isValid;
    };
   
    useEventListener('keyup', event => {
        if (event.key === 'Enter')
            handleCreateAccountButtonClick();
    });
   
    return <>
        <fieldset style={{
            border: 0,
            margin: 0,
            padding: 0,
        }}>
            <Div isRow={true}>
                <Div cols={12}>
                    <H1>
                        <TranslatedSpan english={'Create an account'}/>
                    </H1>
                    <ApiErrorAlert errorMessage={getApiErrorMessage()}/>
                    <legend>
                        <Div paddingTopSM={1} paddingTopMD={2} paddingTopLG={2} paddingTopXL={4}>
                            <H6>
                                <TranslatedSpan english={'Step'}/> 3 <TranslatedSpan english={'of'}/> 3
                            </H6>
                        </Div>
                        <H2 style={{marginTop: 8}}>
                            <TranslatedSpan english={'Your account details'}/>
                        </H2>
                    </legend>
                    <ValidationAlerts
                        heading={`${translate('Error')}: ${translate('Complete these items to sign up for an account.')}`}
                        validationMessages={validationMessages.get()}
                    />
                    <H3>
                        <TranslatedSpan english={'Create username and password'}/>
                    </H3>
                </Div>
            </Div>
            <MDAndLargerDiv>
                <Div isRow={true}>
                    <Div
                        cols={7}
                        paddingRight={2} paddingRightSM={4} paddingRightMD={6} paddingRightLG={7}
                    >
                        <Div isRow={true}>
                            <Div
                                cols={12}
                                paddingRight={2} paddingRightSM={4} paddingRightMD={6} paddingRightLG={7}
                            >
                                {getUsernameInputField()}
                            </Div>
                        </Div>
                        <Div isRow={true}>
                            <Div
                                cols={12}
                                paddingRight={2} paddingRightSM={4} paddingRightMD={6} paddingRightLG={7}
                            >
                                {getPasswordSection()}
                            </Div>
                        </Div>
                        <Div isRow={true}>
                            <Div
                                cols={12}
                                paddingRight={2} paddingRightSM={4} paddingRightMD={6} paddingRightLG={7}
                            >
                                {getConfirmPasswordSection()}
                            </Div>
                        </Div>
                        <Div
                            isRow={true}
                            paddingTop={5}
                        >
                            <Div cols={12}>
                                {getSecureYourAccountH3()}
                            </Div>
                        </Div>
                        <Div isRow={true}>
                            <Div
                                cols={12}
                                paddingRight={2} paddingRightSM={4} paddingRightMD={6} paddingRightLG={7}
                            >
                                {getSecretQuestionDropdown()}
                                {getSecretAnswerInputField()}
                            </Div>
                        </Div>
                    </Div>
                    <Div
                        cols={5}
                        paddingLeft={1} paddingLeftMD={2} paddingLeftLG={3} paddingLeftXL={4}
                        style={{
                            borderLeftColor: the.color.separatorGrey,
                            borderLeftStyle: 'solid',
                            borderLeftWidth: 1,
                            fontFamily: 'Rubik',
                            fontSize: 14,
                            lineHeight: '18px',
                        }}
                    >
                        <p style={{fontWeight: 500}}>
                            <TranslatedSpan english={'Username guidelines'}/>
                        </p>
                        {getUsernameGuidelines()}
                        <p style={{fontWeight: 500}}>
                            <TranslatedSpan english={'Password guidelines'}/>
                        </p>
                        {getPasswordGuidelines()}
                    </Div>
                </Div>
            </MDAndLargerDiv>
            <XSAndSMDiv>
                <Div isRow={true}>
                    <Div cols={12}>
                        {getUsernameInputField()}
                        <div style={{fontSize: '0.9em'}}>
                            <Button
                                aria-label={translate(usernameGuidelinesExpanded.get() ? 'Collapse' : 'Expand')}
                                color={'blue'}
                                displayAs={'link'}
                                onClick={() => usernameGuidelinesExpanded.set(!usernameGuidelinesExpanded.get())}
                                style={{padding: 0}}
                            >
                                <TranslatedSpan english={'Username guidelines'}/>
                                <Caret up={passwordGuidelinesExpanded.get()}/>
                            </Button>
                            <div style={{display: usernameGuidelinesExpanded.get() ? 'inherit' : 'none'}}>
                                {getUsernameGuidelines()}
                            </div>
                        </div>
                    </Div>
                </Div>
                <Div isRow={true}>
                    <Div cols={12}>
                        {getPasswordSection()}
                        <div style={{fontSize: '0.9em'}}>
                            <Button
                                aria-label={translate(passwordGuidelinesExpanded.get() ? 'Collapse' : 'Expand')}
                                color={'blue'}
                                displayAs={'link'}
                                onClick={() => passwordGuidelinesExpanded.set(!passwordGuidelinesExpanded.get())}
                                style={{padding: 0}}
                            >
                                <TranslatedSpan english={'Password guidelines'}/>
                                <Caret up={passwordGuidelinesExpanded.get()}/>
                            </Button>
                            <div style={{display: passwordGuidelinesExpanded.get() ? 'inherit' : 'none'}}>
                                {getPasswordGuidelines()}
                            </div>
                        </div>
                    </Div>
                </Div>
                <Div isRow={true}>
                    <Div cols={12}>
                        {getConfirmPasswordSection()}
                    </Div>
                </Div>
                <Div isRow={true}>
                    <Div
                        cols={12}
                        style={{paddingTop: 24}}
                    >
                        {getSecureYourAccountH3()}
                    </Div>
                </Div>
                <Div isRow={true}>
                    <Div cols={12}>
                        {getSecretQuestionDropdown()}
                    </Div>
                </Div>
                <Div isRow={true}>
                    <Div cols={12}>
                        {getSecretAnswerInputField()}
                    </Div>
                </Div>
            </XSAndSMDiv>
        </fieldset>
        <StackingButtons
            button1={getBackButton()}
            button2={getCreateAccountButton()}
        />
    </>;
}

export default YourAccountDetails;